import { Component, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ApiService } from '../../../core/services/api.service';
import {
  NotificationAudienceFilters,
  NotificationAudienceSampleUser,
  NotificationChannel,
  NotificationPreviewResult,
  SendNotificationPayload,
  SendNotificationResult,
  UserRow,
} from '../../../core/models/api.models';

type TriState = 'any' | 'true' | 'false';
type AudienceMode = 'single' | 'specific' | 'all' | 'filters';

@Component({
  selector: 'app-send-notification',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './send-notification.component.html',
  styleUrl: './send-notification.component.scss',
})
export class SendNotificationComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);

  readonly previewing = signal(false);
  readonly sending = signal(false);
  readonly loadingUsers = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly preview = signal<NotificationPreviewResult | null>(null);
  readonly audienceMode = signal<AudienceMode>('single');
  readonly channel = signal<NotificationChannel>('push');

  readonly users = signal<UserRow[]>([]);
  readonly userSearch = signal('');
  readonly pickerOpen = signal(false);
  readonly selectedUser = signal<UserRow | null>(null);
  readonly selectedUsers = signal<UserRow[]>([]);

  readonly wantsPush = computed(() => {
    const ch = this.channel();
    return ch === 'push' || ch === 'both';
  });

  readonly wantsEmail = computed(() => {
    const ch = this.channel();
    return ch === 'email' || ch === 'both';
  });

  readonly filteredUsers = computed(() => {
    const query = this.userSearch().trim().toLowerCase();
    const selectedIds = new Set(this.selectedUsers().map((u) => u.id));
    const mode = this.audienceMode();

    return this.users().filter((user) => {
      if (mode === 'specific' && selectedIds.has(user.id)) {
        return false;
      }
      if (!query) return true;
      const name = this.userLabel(user).toLowerCase();
      const email = (user.email || '').toLowerCase();
      return name.includes(query) || email.includes(query) || String(user.id).includes(query);
    });
  });

  readonly form = this.fb.nonNullable.group({
    title: ['', [Validators.maxLength(120)]],
    body: [''],
    email_subject: ['', [Validators.maxLength(255)]],
    email_body: [''],
    type: ['promo', Validators.required],
    screen: [''],
    sample_limit: [20, [Validators.min(1), Validators.max(100)]],
    device: ['all'],
    is_verified: ['any' as TriState],
    kyc_status: ['any'],
    has_fcm_token: ['any' as TriState],
    has_email: ['any' as TriState],
    has_cards: ['any' as TriState],
    has_transactions: ['any' as TriState],
    has_rent_payments: ['any' as TriState],
    has_wallet: ['any' as TriState],
    has_beneficiaries: ['any' as TriState],
  });

  ngOnInit(): void {
    this.loadUsers();
    this.syncChannelValidators();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.user-picker')) {
      this.pickerOpen.set(false);
    }
  }

  setChannel(channel: NotificationChannel): void {
    this.channel.set(channel);
    this.syncChannelValidators();
    this.error.set(null);
    this.success.set(null);
  }

  setAudienceMode(mode: AudienceMode): void {
    this.audienceMode.set(mode);
    this.preview.set(null);
    this.error.set(null);
    this.success.set(null);
    this.userSearch.set('');
    this.pickerOpen.set(false);
  }

  openPicker(): void {
    this.pickerOpen.set(true);
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.userSearch.set(value);
    this.pickerOpen.set(true);
  }

  selectSingleUser(user: UserRow): void {
    this.selectedUser.set(user);
    this.userSearch.set('');
    this.pickerOpen.set(false);
    this.error.set(null);
  }

  clearSingleUser(): void {
    this.selectedUser.set(null);
  }

  addSpecificUser(user: UserRow): void {
    if (this.selectedUsers().some((u) => u.id === user.id)) return;
    this.selectedUsers.update((rows) => [...rows, user]);
    this.userSearch.set('');
    this.error.set(null);
  }

  removeSpecificUser(userId: number): void {
    this.selectedUsers.update((rows) => rows.filter((u) => u.id !== userId));
  }

  userLabel(user: UserRow): string {
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    return name || 'Unnamed user';
  }

  previewAudience(): void {
    const filters = this.buildFilters();
    if (!filters) return;

    this.previewing.set(true);
    this.error.set(null);
    this.success.set(null);

    this.api
      .previewNotificationAudience({
        sample_limit: this.form.getRawValue().sample_limit,
        filters,
      })
      .subscribe({
        next: (result) => {
          this.preview.set(result);
          this.previewing.set(false);
        },
        error: (err: { message?: string }) => {
          this.error.set(err?.message || 'Failed to preview audience');
          this.previewing.set(false);
        },
      });
  }

  send(): void {
    if (this.sending()) return;

    this.syncChannelValidators();
    const wantsPush = this.wantsPush();
    const wantsEmail = this.wantsEmail();

    if (wantsPush && this.form.controls.title.invalid) {
      this.form.controls.title.markAsTouched();
      this.error.set('Push title is required');
      return;
    }
    if (wantsEmail && this.form.controls.email_subject.invalid) {
      this.form.controls.email_subject.markAsTouched();
      this.error.set('Email subject is required');
      return;
    }

    const filters = this.buildFilters();
    if (!filters) return;

    this.sending.set(true);
    this.error.set(null);
    this.success.set(null);

    const value = this.form.getRawValue();
    const data: Record<string, string> = {};
    if (wantsPush && value.screen.trim()) {
      data['screen'] = value.screen.trim();
    }

    const payload: SendNotificationPayload = {
      channel: this.channel(),
      type: value.type,
      filters,
    };

    if (wantsPush) {
      payload.title = value.title.trim();
      payload.body = value.body;
      if (Object.keys(data).length) payload.data = data;
    }
    if (wantsEmail) {
      payload.email_subject = value.email_subject.trim();
      payload.email_body = value.email_body;
    }

    this.api.sendNotificationCampaign(payload).subscribe({
      next: (result: SendNotificationResult) => {
        this.sending.set(false);
        this.success.set(this.formatSendResult(result));
        this.preview.set(null);
        setTimeout(() => {
          void this.router.navigate(['/notifications']);
        }, 900);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to send campaign');
        this.sending.set(false);
      },
    });
  }

  sampleRows(): NotificationAudienceSampleUser[] {
    return this.preview()?.sample || [];
  }

  private formatSendResult(result: SendNotificationResult): string {
    const parts = [`Sent ${result.sent_count} delivery(ies) across ${result.total_targeted} targeted`];
    if (result.channel === 'push' || result.channel === 'both' || result.push_sent_count != null) {
      parts.push(`push ${result.push_sent_count ?? 0}`);
    }
    if (result.channel === 'email' || result.channel === 'both' || result.email_sent_count != null) {
      parts.push(`email ${result.email_sent_count ?? 0}`);
    }
    if (result.skipped_no_token) parts.push(`${result.skipped_no_token} no token`);
    if (result.skipped_no_email) parts.push(`${result.skipped_no_email} no email`);
    if (result.failed_count) parts.push(`${result.failed_count} failed`);
    return parts.join(' · ');
  }

  private syncChannelValidators(): void {
    const wantsPush = this.wantsPush();
    const wantsEmail = this.wantsEmail();

    this.form.controls.title.setValidators(
      wantsPush ? [Validators.required, Validators.maxLength(120)] : [Validators.maxLength(120)]
    );
    this.form.controls.email_subject.setValidators(
      wantsEmail ? [Validators.required, Validators.maxLength(255)] : [Validators.maxLength(255)]
    );
    this.form.controls.title.updateValueAndValidity({ emitEvent: false });
    this.form.controls.email_subject.updateValueAndValidity({ emitEvent: false });
  }

  private loadUsers(): void {
    this.loadingUsers.set(true);
    this.api.getUsers(1, 200).subscribe({
      next: (data) => {
        this.users.set(data.rows || []);
        this.loadingUsers.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load users');
        this.loadingUsers.set(false);
      },
    });
  }

  private buildFilters(): NotificationAudienceFilters | null {
    const mode = this.audienceMode();

    if (mode === 'single') {
      const user = this.selectedUser();
      if (!user) {
        this.error.set('Select a user from the dropdown');
        return null;
      }
      return { user_ids: [user.id] };
    }

    if (mode === 'specific') {
      const ids = this.selectedUsers().map((u) => u.id);
      if (!ids.length) {
        this.error.set('Select at least one user');
        return null;
      }
      return { user_ids: ids };
    }

    if (mode === 'all') {
      return {};
    }

    const value = this.form.getRawValue();
    const filters: NotificationAudienceFilters = {};

    if (value.device && value.device !== 'all') {
      filters.device = value.device;
    } else if (value.device === 'all') {
      filters.device = 'all';
    }

    const verified = this.triToNumber(value.is_verified);
    if (verified !== undefined) filters.is_verified = verified;

    if (value.kyc_status && value.kyc_status !== 'any') {
      filters.kyc_status = value.kyc_status;
    }

    const boolFields: Array<{ key: keyof NotificationAudienceFilters; value: TriState }> = [
      { key: 'has_fcm_token', value: value.has_fcm_token },
      { key: 'has_email', value: value.has_email },
      { key: 'has_cards', value: value.has_cards },
      { key: 'has_transactions', value: value.has_transactions },
      { key: 'has_rent_payments', value: value.has_rent_payments },
      { key: 'has_wallet', value: value.has_wallet },
      { key: 'has_beneficiaries', value: value.has_beneficiaries },
    ];

    for (const field of boolFields) {
      const parsed = this.triToBool(field.value);
      if (parsed !== undefined) {
        Object.assign(filters, { [field.key]: parsed });
      }
    }

    return filters;
  }

  private triToBool(value: TriState): boolean | undefined {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return undefined;
  }

  private triToNumber(value: TriState): 0 | 1 | undefined {
    if (value === 'true') return 1;
    if (value === 'false') return 0;
    return undefined;
  }
}
